/**
 * Job polling (M3 jobs on Cloudflare Workflows) — the local app's 1.5 s `episodeStatus` loop
 * (Workspace.jsx:454-490) re-homed on `api.jobGet`. Pure pill copy lives here too
 * (`jobPillModel`, tested) so `components/watch/JobPill` stays thin.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Job } from '@nelnela/contracts';
import { api } from './api';

export const JOB_POLL_INTERVAL_MS = 1500;
/** consecutive failed polls before we report `stalled` and back off (last known job kept) */
export const JOB_MAX_CONSECUTIVE_FAILURES = 5;
/** while stalled we keep polling, this many times slower (10× → every 15 s by default) */
export const JOB_STALLED_BACKOFF = 10;

export const isJobActive = (j: Pick<Job, 'status'> | null | undefined): boolean =>
  j?.status === 'queued' || j?.status === 'running';

export interface UseJobOptions {
  intervalMs?: number;
  /** fired once when the job reaches `done` */
  onDone?: (job: Job) => void;
  /** fired once when the job reaches `error` */
  onError?: (job: Job) => void;
}

export interface UseJobResult {
  /** the last job snapshot fetched for the current id (null until the first poll answers) */
  job: Job | null;
  /** true while `jobGet` is being polled (a non-null id whose job is queued/running or unknown) */
  polling: boolean;
  /**
   * true after 5 consecutive failed polls — `job` is the last known snapshot and we keep polling
   * at 10× the interval until a poll succeeds again (then false)
   */
  stalled: boolean;
  /** swap the job being watched (null stops); the `jobId` argument also drives it */
  setJobId: (id: string | null) => void;
}

/**
 * Poll `api.jobGet(id)` every 1.5 s while the job is queued/running. Stops on done/error/inert
 * (fires `onDone`/`onError` once per job id; `inert` fires nothing — nothing happened). Transient
 * fetch failures keep polling; after 5 in a row we keep the last known job, set `stalled`, and
 * keep polling at 10× the interval; the next successful poll clears `stalled` and restores the
 * normal cadence — so a job that finishes while we were offline is still noticed.
 */
export function useJob(jobId: string | null, opts: UseJobOptions = {}): UseJobResult {
  const { intervalMs = JOB_POLL_INTERVAL_MS } = opts;
  const [id, setId] = useState<string | null>(jobId);
  const [job, setJob] = useState<Job | null>(null);
  const [polling, setPolling] = useState<boolean>(jobId !== null);
  const [stalled, setStalled] = useState(false);
  // Latest callbacks without re-arming the interval when the parent re-renders.
  const cbRef = useRef(opts);
  cbRef.current = opts;

  // The prop is the primary driver; `setJobId` is the escape hatch for a job started from a
  // click handler (the caller usually keeps both in sync — either path is fine).
  useEffect(() => setId(jobId), [jobId]);
  const setJobId = useCallback((next: string | null) => setId(next), []);

  useEffect(() => {
    if (!id) {
      setJob(null);
      setPolling(false);
      setStalled(false);
      return;
    }
    let cancelled = false;
    let inflight = false;
    let failures = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    setJob(null);
    setStalled(false);
    setPolling(true);

    const stop = () => {
      if (timer !== null) clearTimeout(timer);
      timer = null;
      setPolling(false);
    };
    // One timeout at a time (not setInterval) so the cadence can back off while stalled.
    const schedule = () => {
      if (cancelled) return;
      if (timer !== null) clearTimeout(timer);
      const delay =
        failures >= JOB_MAX_CONSECUTIVE_FAILURES ? intervalMs * JOB_STALLED_BACKOFF : intervalMs;
      timer = setTimeout(() => void tick(), delay);
    };
    const tick = async () => {
      if (cancelled || inflight) return;
      inflight = true;
      let finished = false;
      try {
        const j = await api.jobGet(id);
        if (cancelled) return;
        failures = 0;
        setStalled(false);
        setJob(j);
        if (!isJobActive(j)) {
          finished = true;
          stop();
          if (j.status === 'done') cbRef.current.onDone?.(j);
          else if (j.status === 'error') cbRef.current.onError?.(j);
        }
      } catch {
        if (cancelled) return;
        failures += 1;
        if (failures >= JOB_MAX_CONSECUTIVE_FAILURES) setStalled(true);
      } finally {
        inflight = false;
        if (!cancelled && !finished) schedule();
      }
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [id, intervalMs]);

  return { job, polling, stalled, setJobId };
}

// --- pill copy (pure) ---------------------------------------------------------

export type JobPillTone = 'progress' | 'error' | 'done';
export interface JobPillModel {
  tone: JobPillTone;
  /** the visible message, e.g. "Cleaning up… 40%" / "Cleanup failed — raw transcript kept" */
  text: string;
  /** 0..1 fill for the progress bar (progress tone only) */
  progress: number;
  /** show the Retry action (error tone only) */
  retry: boolean;
}

const VERB: Record<string, { running: string; failed: string }> = {
  caption_cleanup: { running: 'Cleaning up', failed: 'Cleanup failed — raw transcript kept' },
  translate: { running: 'Translating', failed: 'Translation failed' },
  availability_check: { running: 'Checking availability', failed: 'Availability check failed' },
};
const DONE: Record<string, string> = {
  caption_cleanup: 'Cleaned up ✓',
  translate: 'Translated ✓',
  availability_check: 'Checked ✓',
};
const humanize = (kind: string) => kind.replace(/_/g, ' ');

/** the calm line the pill shows while polls keep failing (`stalled`) — the job may well be fine */
export const JOB_STALLED_TEXT = 'Lost track of the job — retrying…';

/**
 * What the pill says for this job — null when nothing should be shown (no job / inert).
 * `stalled` (5 failed polls in a row) swaps the running text for a calm retrying line.
 */
export function jobPillModel(
  job: Pick<Job, 'kind' | 'status' | 'progress'> | null,
  opts: { stalled?: boolean } = {},
): JobPillModel | null {
  if (!job || job.status === 'inert') return null;
  const p = Number.isFinite(job.progress) ? Math.min(1, Math.max(0, job.progress)) : 0;
  const v = VERB[job.kind];
  if (job.status === 'queued' || job.status === 'running') {
    const verb = v?.running ?? humanize(job.kind);
    return {
      tone: 'progress',
      text: opts.stalled ? JOB_STALLED_TEXT : `${verb}… ${Math.round(p * 100)}%`,
      progress: p,
      retry: false,
    };
  }
  if (job.status === 'error') {
    return {
      tone: 'error',
      text: v?.failed ?? `${humanize(job.kind)} failed`,
      progress: p,
      retry: true,
    };
  }
  return {
    tone: 'done',
    text: DONE[job.kind] ?? `${humanize(job.kind)} ✓`,
    progress: 1,
    retry: false,
  };
}
